import { X } from 'lucide-react';
import type { ComponentProps } from 'react';

import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { IconTouchable, type IconTouchableProps } from '@vs-shop/ui/iconTouchable';
import { clsxm } from '@vs-shop/utils';

/**
 * The chips a listing shows for the filters a shopper has applied, each naming
 * the facet and the value and carrying the control that takes it off again,
 * with the control that clears them all at the end of the row.
 *
 *   <FilterChips aria-label={activeFiltersLabel}>
 *     {chips.map((chip) => (
 *       <FilterChip key={chip.id}>
 *         <FilterChipCopy>
 *           <FilterChipLabel>{chip.facet}</FilterChipLabel>
 *           <FilterChipValue>{chip.value}</FilterChipValue>
 *         </FilterChipCopy>
 *         <FilterChipRemove aria-label={removeLabel} onClick={…} />
 *       </FilterChip>
 *     ))}
 *     <FilterChipsClearAll onClick={…}>{clearAllLabel}</FilterChipsClearAll>
 *   </FilterChips>
 *
 * One chip for every kind of filter — a category, a brand, a yes/no
 * attribute, a range, a text value, a store — because a chip is the same
 * statement whatever it stands for: this facet, this value, and a way out.
 * What the facet is called, how the value is formatted and what removing it
 * does are the tenant's, and arrive as children and handlers.
 *
 * No `variant` anywhere: a chip paints one way, and what differs between
 * listings is how many there are.
 *
 * Server-first: every part is a plain component. The remove control is a
 * native `<button>` whose handler the tenant attaches, so a composition that
 * wires it is a client tree by its own choice.
 */

export type FilterChipsProps = ComponentProps<'ul'>;

/** The list of applied filters. */
export const FilterChips = ({ className, ...props }: FilterChipsProps) => (
  <ul
    data-slot="filter-chips"
    className={clsxm('lap:gap-3 flex flex-wrap gap-2', className)}
    {...props}
  />
);

/**
 * `w-max` and `whitespace-nowrap` together: a chip is as wide as what it says
 * and never breaks a value across lines — the row wraps, the chip does not.
 */
const filterChipClasses =
  'flex w-max items-center gap-4 rounded-sm bg-background-inverted-intense px-4 py-2 whitespace-nowrap lap:gap-8';

export type FilterChipProps = ComponentProps<'div'>;

/**
 * One applied filter: its copy at the leading end, its remove control at the
 * trailing end, on an inverted ground so the row reads as state rather than as
 * more options.
 */
export const FilterChip = ({ className, ...props }: FilterChipProps) => (
  <div data-slot="filter-chip" className={clsxm(filterChipClasses, className)} {...props} />
);

export type FilterChipCopyProps = ComponentProps<'div'>;

/**
 * The facet's name and the value, stacked on a phone and side by side from
 * `lap` up, where a row has the width for both.
 */
export const FilterChipCopy = ({ className, ...props }: FilterChipCopyProps) => (
  <div
    data-slot="filter-chip-copy"
    className={clsxm('lap:flex-row lap:gap-3 flex flex-col gap-0.5', className)}
    {...props}
  />
);

export type FilterChipLabelProps = ComponentProps<'span'>;

/** The facet's name, set back so the value reads first. */
export const FilterChipLabel = ({ className, ...props }: FilterChipLabelProps) => (
  <span
    data-slot="filter-chip-label"
    className={clsxm('text-foreground-inverted/60', className)}
    {...props}
  />
);

export type FilterChipValueProps = ComponentProps<'span'>;

/** The value the shopper chose. */
export const FilterChipValue = ({ className, ...props }: FilterChipValueProps) => (
  <span
    data-slot="filter-chip-value"
    className={clsxm('text-foreground-inverted font-semibold', className)}
    {...props}
  />
);

export type FilterChipRemoveProps = Omit<
  IconTouchableProps<'button'>,
  'as' | 'children' | 'type'
> & {
  /**
   * Required: the mark is the control's entire visible content, so without it
   * assistive technology has nothing to call the button.
   */
  readonly 'aria-label': string;
};

/**
 * The control that takes the filter off, and the mark inside it — "take this
 * off" is the same statement in every tenant, so the part renders it.
 *
 * `IconTouchable` gives the mark a touch target the chip's own height would
 * not. Always `type="button"`: a chip row inside a form must not submit it.
 */
export const FilterChipRemove = ({ className, ...props }: FilterChipRemoveProps) => (
  <IconTouchable
    type="button"
    data-slot="filter-chip-remove"
    className={clsxm(className)}
    {...props}
  >
    <X className="text-primary size-4 shrink-0" />
  </IconTouchable>
);

export type FilterChipsClearAllProps = Omit<ButtonProps, 'type'>;

/**
 * The control that clears every filter at once, closing the row.
 *
 * `h-auto min-h-10` rather than a fixed height: it shares the row with chips
 * that are two lines tall on a phone, and stretches to meet them.
 */
export const FilterChipsClearAll = ({ className, ...props }: FilterChipsClearAllProps) => (
  <Button
    type="button"
    data-slot="filter-chips-clear-all"
    className={clsxm(
      'flex h-auto min-h-10 w-max rounded-sm text-xs tracking-widest whitespace-nowrap uppercase',
      className
    )}
    {...props}
  />
);
